import { useState } from 'react';
import {
  deviceTypes,
  railsToRecheck,
  refineExtraction,
  type AiService,
  type BoardDevice,
  type BoardExtraction,
  type ImageMediaType,
  isAiUnavailable,
} from '@elec/ai';
import type { MessageKey, Translate } from '@elec/i18n';
import type { Platform } from '@elec/platform';
import { cropAiImage, prepareAiImage } from './photos.ts';
import { Dialog } from './ui.tsx';

const MEDIA: Record<string, ImageMediaType> = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  webp: 'image/webp',
};

function DemoNotice({ ai, text }: { ai: AiService; text: string }) {
  return ai.mode === 'demo' ? (
    <p className="notice" data-testid="ai-demo">
      {text}
    </p>
  ) : null;
}

/** Board photo → editable device list → single-line diagram. */
export function BoardImportDialog(props: {
  ai: AiService;
  platform: Platform;
  t: Translate;
  onClose(): void;
  onImport(ex: BoardExtraction): void;
}) {
  const { ai, t } = props;
  const [photo, setPhoto] = useState<{ url: string; bytes: Uint8Array; mime: ImageMediaType } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ex, setEx] = useState<BoardExtraction | null>(null);
  const [rechecking, setRechecking] = useState(false);

  const pick = async () => {
    const f = await props.platform.openFile({ extensions: Object.keys(MEDIA), description: 'Photo' });
    if (!f) return;
    // Smaller image: much faster for the model, and a lighter upload from the site.
    const small = await prepareAiImage(
      f.bytes,
      MEDIA[f.name.toLowerCase().split('.').pop() ?? ''] ?? 'image/jpeg',
    );
    setPhoto({
      url: URL.createObjectURL(new Blob([small.bytes.slice().buffer as ArrayBuffer], { type: small.mime })),
      bytes: small.bytes,
      mime: small.mime,
    });
    setEx(null);
  };
  const analyse = async () => {
    setBusy(true);
    setError(null);
    try {
      const first = await ai.extractBoard(photo?.bytes ?? new Uint8Array(), photo?.mime ?? 'image/jpeg');
      // A device that looks misread on the whole board: read its rail again, close up.
      if (photo && ai.mode === 'remote' && railsToRecheck(first).length) {
        setRechecking(true);
        const r = await refineExtraction(
          first,
          async (top, bottom) => {
            const band = await cropAiImage(photo.bytes, photo.mime, top, bottom);
            return ai.extractBoard(band.bytes, band.mime);
          },
          (rail, labels) => t('ai.recheckNote', { rail, labels: labels.join(', ') }),
        );
        setEx(r.extraction);
      } else setEx(first);
    } catch (e) {
      setError(isAiUnavailable(e) ? t('ai.unavailable') : t('ai.error', { msg: (e as Error).message }));
    } finally {
      setBusy(false);
      setRechecking(false);
    }
  };
  const setDev = (i: number, patch: Partial<BoardDevice>) =>
    setEx(
      (x) =>
        x && { ...x, devices: x.devices.map((d, j) => (j === i ? { ...d, ...patch, confidence: 1 } : d)) },
    );
  const num = (v: string) => (v === '' ? null : Number(v));

  return (
    <Dialog
      title={t('ai.board').replace('…', '')}
      wide
      onClose={props.onClose}
      actions={
        <>
          <button onClick={props.onClose}>{t('dialog.cancel')}</button>
          {ex ? (
            <button className="primary" onClick={() => props.onImport(ex)} data-testid="ai-import">
              {t('ai.import')}
            </button>
          ) : (
            <button
              className="primary"
              onClick={analyse}
              disabled={busy || (!photo && ai.mode === 'remote')}
              data-testid="ai-analyse"
            >
              {rechecking ? t('ai.rechecking') : busy ? t('ai.analysing') : t('ai.analyse')}
            </button>
          )}
        </>
      }
    >
      <DemoNotice ai={ai} text={t('ai.demoBanner')} />
      <div className="ai-photo">
        {photo ? <img src={photo.url} alt="" /> : <div className="ai-placeholder" />}
        <button onClick={pick}>{t('ai.choosePhoto')}</button>
      </div>
      {error && <p className="warn">{error}</p>}
      {ex && (
        <>
          <p className="small">{t('ai.review')}</p>
          <div className="table-scroll ai-table">
            <table>
              <thead>
                <tr>
                  {(
                    [
                      'ai.row',
                      'ai.pos',
                      'ai.type',
                      'ai.rating',
                      'ai.curve',
                      'ai.sens',
                      'ai.rcdType',
                      'ai.label',
                      'ai.confidence',
                    ] as MessageKey[]
                  ).map((k) => (
                    <th key={k}>{t(k)}</th>
                  ))}
                  <th />
                </tr>
              </thead>
              <tbody>
                {ex.devices.map((d, i) => (
                  <tr key={i} className={d.confidence < 0.8 ? 'uncertain' : ''} data-testid={`ai-row-${i}`}>
                    <td>{d.row}</td>
                    <td>{d.index}</td>
                    <td>
                      <select
                        value={d.deviceType}
                        onChange={(e) =>
                          setDev(i, { deviceType: e.target.value as BoardDevice['deviceType'] })
                        }
                      >
                        {deviceTypes.map((v) => (
                          <option key={v} value={v}>
                            {t(`ai.dt.${v}` as MessageKey)}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <input
                        className="cell"
                        type="number"
                        style={{ width: 52 }}
                        value={d.ratingA ?? ''}
                        onChange={(e) => setDev(i, { ratingA: num(e.target.value) })}
                      />
                    </td>
                    <td>
                      <select
                        value={d.curve ?? ''}
                        onChange={(e) =>
                          setDev(i, { curve: (e.target.value || null) as BoardDevice['curve'] })
                        }
                      >
                        <option value="">—</option>
                        {['B', 'C', 'D'].map((v) => (
                          <option key={v}>{v}</option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <input
                        className="cell"
                        type="number"
                        style={{ width: 56 }}
                        value={d.sensitivityMa ?? ''}
                        onChange={(e) => setDev(i, { sensitivityMa: num(e.target.value) })}
                      />
                    </td>
                    <td>
                      <select
                        value={d.rcdType ?? ''}
                        onChange={(e) =>
                          setDev(i, { rcdType: (e.target.value || null) as BoardDevice['rcdType'] })
                        }
                      >
                        <option value="">—</option>
                        {['AC', 'A', 'F', 'B'].map((v) => (
                          <option key={v}>{v}</option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <input
                        className="cell"
                        style={{ width: 150 }}
                        value={d.label ?? ''}
                        onChange={(e) => setDev(i, { label: e.target.value || null })}
                      />
                    </td>
                    <td className="conf">{Math.round(d.confidence * 100)} %</td>
                    <td>
                      <button
                        className="link"
                        title={t('ai.removeRow')}
                        onClick={() => setEx({ ...ex, devices: ex.devices.filter((_, j) => j !== i) })}
                      >
                        ✕
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {ex.notes && (
            <p className="small muted">
              {t('ai.notes')} : {ex.notes}
            </p>
          )}
          <p className="small muted">{t('ai.toFill')}</p>
        </>
      )}
    </Dialog>
  );
}
